import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fetchExclusions, restoreExclusion } from './adminApi';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'revoked', label: 'Restored' },
  { value: 'all', label: 'All' },
];

function when(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function ruleSummary(row) {
  if (row.match_type === 'card_id') {
    return <code>{row.card_id}</code>;
  }
  const extra = row.additional_patterns || [];
  return (
    <span>
      <code>{row.description_pattern}</code>
      {extra.length > 0 && <span className="admin-card-sub">and {extra.map((p) => <code key={p}>{p}</code>)}</span>}
      {row.sport && <span className="admin-card-sub">sport = {row.sport}</span>}
      {row.number && <span className="admin-card-sub">number = {row.number}</span>}
    </span>
  );
}

function AdminExclusions({ refreshToken }) {
  const [status, setStatus] = useState('active');
  const [queryText, setQueryText] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  // Two-click confirm, as SavedFiltersSidebar does for its deletes.
  const [confirmingId, setConfirmingId] = useState(null);
  const noticeTimer = useRef(null);

  const load = useCallback(async (signal) => {
    setLoading(true);
    setError('');
    try {
      setData(await fetchExclusions({ status, q: queryText, page, per_page: 50 }, signal));
    } catch (loadError) {
      if (loadError.name !== 'AbortError') setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, [status, queryText, page]);

  useEffect(() => {
    const controller = new AbortController();
    const handle = setTimeout(() => load(controller.signal), 250);
    return () => { clearTimeout(handle); controller.abort(); };
  }, [load, refreshToken]);

  useEffect(() => () => { if (noticeTimer.current) clearTimeout(noticeTimer.current); }, []);

  const handleRestore = async (row) => {
    try {
      const result = await restoreExclusion(row.id);
      setNotice(result.note || 'Exclusion restored.');
      if (noticeTimer.current) clearTimeout(noticeTimer.current);
      noticeTimer.current = setTimeout(() => setNotice(''), 8000);
      setConfirmingId(null);
      load();
    } catch (restoreError) {
      setError(restoreError.message);
    }
  };

  const rows = data?.data || [];
  const counts = data?.counts || { active: 0, revoked: 0 };
  const totalPages = Math.max(Math.ceil((data?.total || 0) / (data?.per_page || 50)), 1);

  if (data?.migration_pending) {
    return (
      <p className="admin-error" role="alert">
        The <code>card_exclusions</code> table does not exist yet. Apply
        {' '}<code>migrations/create_admin_console_tables.sql</code> to this database.
      </p>
    );
  }

  return (
    <div className="admin-panel">
      <div className="admin-toolbar">
        <input
          className="admin-input admin-input--grow"
          type="search"
          value={queryText}
          onChange={(event) => { setQueryText(event.target.value); setPage(1); }}
          placeholder="Search card, pattern, or reason"
          aria-label="Search exclusions"
        />
        <select
          className="admin-input"
          value={status}
          onChange={(event) => { setStatus(event.target.value); setPage(1); }}
          aria-label="Status"
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>

      {notice && <p className="admin-notice" role="status">{notice}</p>}
      {error && <p className="admin-error" role="alert">{error}</p>}

      <p className="admin-meta-line">
        {loading ? 'Loading…' : `${counts.active} active · ${counts.revoked} restored`}
      </p>

      <div className="admin-table-scroll">
        <table className="admin-table">
          <thead>
            <tr>
              <th scope="col">Card / rule</th>
              <th scope="col">Type</th>
              <th scope="col">Reason</th>
              <th scope="col">Removed by</th>
              <th scope="col">When</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={6} className="admin-empty">No exclusions match those filters.</td></tr>
            )}
            {rows.map((row) => (
              <tr key={row.id} className={row.revoked_at ? 'admin-row--revoked' : undefined}>
                <td>
                  {row.card_description && (
                    <span className="admin-card-description">{row.card_description}</span>
                  )}
                  <span className="admin-card-sub">{ruleSummary(row)}</span>
                </td>
                <td>
                  {row.match_type === 'card_id' ? 'Single card' : 'Pattern'}
                  <span className="admin-card-sub">{row.source}</span>
                </td>
                <td>{row.reason || '—'}</td>
                <td>{row.created_by || '—'}</td>
                <td>
                  {when(row.created_at)}
                  {row.revoked_at && (
                    <span className="admin-card-sub">
                      restored {when(row.revoked_at)} by {row.revoked_by || '—'}
                    </span>
                  )}
                </td>
                <td className="admin-actions">
                  {row.revoked_at ? (
                    <span className="admin-card-sub">Restored</span>
                  ) : confirmingId === row.id ? (
                    <>
                      <button type="button" className="admin-button admin-button--danger" onClick={() => handleRestore(row)}>
                        Confirm restore
                      </button>
                      <button type="button" className="admin-button" onClick={() => setConfirmingId(null)}>
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button type="button" className="admin-button" onClick={() => setConfirmingId(row.id)}>
                      Restore
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="admin-pager">
          <button type="button" className="admin-button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </button>
          <span>Page {page} of {totalPages}</span>
          <button type="button" className="admin-button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default AdminExclusions;
